/**
 * MeetingFormModal — create/edit a meeting: title, date+time, duration,
 * location/link, notes, client/engagement links, staff attendees (multi-check)
 * and free-text external emails.
 */

import { useMemo, useState } from 'react';
import type {
  CreateMeetingInput, EngagementOutput, MeetingOutput, UpdateMeetingInput, UserOutput,
} from '@1financial/shared';
import { SearchableSelect } from './SearchableSelect.js';
import { ClientSearchSelect } from './ClientSearchSelect.js';
import { DateInput } from './DateInput.js';

const DURATIONS = [15, 30, 45, 60, 90, 120];

function toLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toLocalTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function MeetingFormModal({
  initial,
  presetDate,
  users,
  engagements,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  initial?: MeetingOutput | null;
  /** Pre-selected calendar day (YYYY-MM-DD) when creating from a day cell. */
  presetDate?: string;
  users: UserOutput[];
  engagements: EngagementOutput[];
  onClose: () => void;
  onSubmit: (input: CreateMeetingInput | UpdateMeetingInput) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = !!initial;
  const start = initial ? new Date(initial.startAt) : null;
  const [title, setTitle] = useState(initial?.title ?? '');
  const [date, setDate] = useState(start ? toLocalDate(start) : presetDate ?? toLocalDate(new Date()));
  const [time, setTime] = useState(start ? toLocalTime(start) : '10:00');
  const [durationMin, setDurationMin] = useState(initial?.durationMin ?? 30);
  const [location, setLocation] = useState(initial?.location ?? '');
  const [meetingUrl, setMeetingUrl] = useState(initial?.meetingUrl ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [clientId, setClientId] = useState(initial?.clientId ?? '');
  const [engagementId, setEngagementId] = useState(initial?.engagementId ?? '');
  const [attendeeIds, setAttendeeIds] = useState<Set<string>>(
    () => new Set(initial?.attendees.filter((a) => a.userId && a.userId !== initial.organizerId).map((a) => a.userId!) ?? []),
  );
  const [externalEmails, setExternalEmails] = useState(
    initial?.attendees.filter((a) => a.email).map((a) => a.email!).join(', ') ?? '',
  );

  const engagementOptions = useMemo(
    () => engagements
      .filter((e) => !clientId || e.clientId === clientId)
      .map((e) => ({ value: e.id, label: `${e.title ?? e.type ?? 'Engagement'} — ${e.clientName ?? ''}` })),
    [engagements, clientId],
  );
  const activeUsers = useMemo(() => users.filter((u) => u.status === 'ACTIVE'), [users]);

  function toggleAttendee(id: string) {
    setAttendeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const startAt = new Date(`${date}T${time}:00`);
    if (Number.isNaN(startAt.getTime())) return;
    const emails = externalEmails.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    const payload = {
      title: title.trim(),
      startAt: startAt.toISOString(),
      durationMin,
      location: location.trim() || null,
      meetingUrl: meetingUrl.trim() || null,
      notes: notes.trim() || null,
      clientId: clientId || null,
      engagementId: engagementId || null,
      attendeeUserIds: [...attendeeIds],
      externalEmails: emails,
    };
    onSubmit(payload);
  }

  const inputCls = 'w-full bg-bg-card border border-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-accent';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{isEdit ? 'Edit meeting' : 'New meeting'}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} className={inputCls} placeholder="e.g. VAT return review with Acme" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Date *</label>
              <DateInput value={date} onChange={(e) => setDate(e.target.value)} required className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Start time *</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Duration</label>
              <select value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} className={inputCls}>
                {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Location</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={300} className={inputCls} placeholder="Office, client site…" />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Meeting link</label>
              <input type="url" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)} className={inputCls} placeholder="https://zoom.us/…" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted mb-1">Client</label>
              {/* OPS 3.5 #11: server-searched picker — the old limit=100 prefetch
                  hid every client past the first page. */}
              <ClientSearchSelect
                value={clientId}
                initialOption={initial?.clientId && initial.clientName
                  ? { id: initial.clientId, name: initial.clientName, company: null, email: null, jurisdiction: null }
                  : null}
                onChange={(v, c) => {
                  setClientId(v);
                  setEngagementId('');
                  // Invite the linked client's email — it's free text, so the
                  // organizer can still remove it before saving.
                  const email = c?.email?.trim();
                  if (email) {
                    setExternalEmails((prev) => {
                      const list = prev.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
                      return list.some((e) => e.toLowerCase() === email.toLowerCase()) ? prev : [...list, email].join(', ');
                    });
                  }
                }}
                placeholder="No client"
              />
            </div>
            <div>
              <label className="block text-xs text-muted mb-1">Engagement</label>
              <SearchableSelect
                options={engagementOptions}
                value={engagementId}
                onChange={setEngagementId}
                placeholder={clientId ? 'No engagement' : 'Pick a client first'}
                emptyMessage="No engagements found"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Staff attendees</label>
            <div className="max-h-32 overflow-y-auto bg-bg-card border border-border rounded-lg p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
              {activeUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-1.5 text-xs text-white px-1.5 py-1 rounded hover:bg-bg-hover cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attendeeIds.has(u.id)}
                    onChange={() => toggleAttendee(u.id)}
                    className="accent-emerald-500"
                  />
                  <span className="truncate">{u.displayName}</span>
                </label>
              ))}
              {activeUsers.length === 0 && <p className="text-xs text-muted italic px-1">Loading users…</p>}
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">External attendees (emails, comma-separated)</label>
            <input value={externalEmails} onChange={(e) => setExternalEmails(e.target.value)} className={inputCls} placeholder="client@example.com, advisor@example.com" />
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Agenda / notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} placeholder="What is this meeting about?" />
          </div>
          <p className="text-xs text-muted">Attendees receive an email with a calendar invite; a reminder goes out 1 hour before start.</p>
          {error && <p className="text-xs text-rose-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="submit" disabled={isPending || !title.trim()} className="bg-accent hover:opacity-90 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
              {isPending ? 'Saving…' : isEdit ? 'Save & notify' : 'Create & send invites'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
